// Generic reading engine — the shared foundation for every "full document is
// visible" reading experience (Guided Reading today; Bionic, Dictionary, Notes,
// Exam/Memory modes tomorrow).
//
// It turns raw document text into a stable, index-addressable model:
//   paragraphs -> sentences -> phrases -> tokens (words)
// Every token has a stable global index (aligned with the app's word index) so
// features can decorate token ranges (highlights), drive a moving guide (units),
// map reading progress, or attach click handlers (dictionary/explanations)
// WITHOUT re-tokenising or coupling to any single feature.

export const SENTENCE_BOUNDARY = /(?<=[.!?\u3002\uFF01\uFF1F\u2026]["')\]]?)\s+/;

const PHRASE_MAX = 6; // max words per phrase unit
const PHRASE_BREAK = /[,;:\u2014\u2013)]$/; // token that closes a phrase
const SENT_END = /[.!?\u2026]$/;

/**
 * Build the full reading model from raw text.
 * @returns {{
 *   paragraphs: Array<{index:number, tokens:Array}>,
 *   sentences: Array<{index:number, tokenStart:number, tokenEnd:number, wordCount:number, paragraphIndex:number}>,
 *   phrases:   Array<{index:number, tokenStart:number, tokenEnd:number, wordCount:number, sentenceIndex:number}>,
 *   tokens:    Array<{i:number, text:string, paragraphIndex:number, sentenceIndex:number, phraseIndex:number}>,
 *   totalTokens:number
 * }}
 */
export function buildDocModel(text) {
    const rawParas = (text || "").split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    const tokens = [];
    const sentences = [];
    const phrases = [];
    const paragraphs = [];

    rawParas.forEach((para, pIdx) => {
        const paraTokens = [];
        const parts = para.split(SENTENCE_BOUNDARY).map((s) => s.trim()).filter(Boolean);
        const sentenceParts = parts.length ? parts : [para];

        sentenceParts.forEach((sentText) => {
            const words = sentText.split(/\s+/).filter(Boolean);
            if (!words.length) return;
            const sIdx = sentences.length;
            const sentTokenStart = tokens.length;

            let phraseTokenStart = tokens.length;
            let phraseWordCount = 0;

            const closePhrase = () => {
                if (phraseWordCount === 0) return;
                phrases.push({
                    index: phrases.length,
                    tokenStart: phraseTokenStart,
                    tokenEnd: tokens.length - 1,
                    wordCount: phraseWordCount,
                    sentenceIndex: sIdx,
                });
                phraseTokenStart = tokens.length;
                phraseWordCount = 0;
            };

            words.forEach((w) => {
                const tok = {
                    i: tokens.length,
                    text: w,
                    paragraphIndex: pIdx,
                    sentenceIndex: sIdx,
                    phraseIndex: phrases.length, // phrase this token will belong to
                };
                tokens.push(tok);
                paraTokens.push(tok);
                phraseWordCount += 1;
                if (PHRASE_BREAK.test(w) || SENT_END.test(w) || phraseWordCount >= PHRASE_MAX) {
                    closePhrase();
                }
            });
            closePhrase();

            sentences.push({
                index: sIdx,
                tokenStart: sentTokenStart,
                tokenEnd: tokens.length - 1,
                wordCount: words.length,
                paragraphIndex: pIdx,
            });
        });

        paragraphs.push({ index: pIdx, tokens: paraTokens });
    });

    return { paragraphs, sentences, phrases, tokens, totalTokens: Math.max(1, tokens.length) };
}

/**
 * Word Anchor Highlight — split a word into head/mid/last character segments so
 * the reading surface can tint anchor letters (a static Bionic-style blick guide,
 * distinct from the moving active word). Returns null for the "off" mode or when
 * the word is too short to split meaningfully.
 *   mode: "2" | "3" | "4" | "2l" | "3l" | "auto"
 */
export function anchorSegments(word, mode) {
    if (!mode || mode === "off") return null;
    const L = word.length;
    if (L <= 1) return null;
    let head;
    let useLast = false;
    if (mode === "auto") head = L <= 3 ? 1 : L <= 6 ? 2 : L <= 9 ? 3 : 4;
    else if (mode === "2l") { head = 2; useLast = true; }
    else if (mode === "3l") { head = 3; useLast = true; }
    else head = parseInt(mode, 10) || 0;
    if (head <= 0) return null;
    // Never let the head swallow the whole word; keep room for a last char if asked.
    head = Math.min(head, useLast ? L - 1 : L);
    if (useLast && L - head >= 1) {
        return { head: word.slice(0, head), mid: word.slice(head, L - 1), last: word.slice(L - 1) };
    }
    return { head: word.slice(0, head), mid: word.slice(head), last: "" };
}

/** Reading units for the moving guide, by granularity. */
export function buildUnits(model, granularity) {
    if (granularity === "word") {
        return model.tokens.map((t) => ({ start: t.i, end: t.i, wordCount: 1 }));
    }
    const src = granularity === "sentence" ? model.sentences : model.phrases;
    return src.map((u) => ({ start: u.tokenStart, end: u.tokenEnd, wordCount: u.wordCount }));
}

export function unitIndexForToken(units, tokenIndex) {
    if (!units.length) return 0;
    for (let i = 0; i < units.length; i++) {
        if (tokenIndex <= units[i].end) return i;
    }
    return units.length - 1;
}

// ------------------------------- highlights ------------------------------- //

// Category priority (highest first). Determines which colour wins when a token
// qualifies for several layers, matching the product's reading-value order:
// Definitions > Core Concepts > Exam-Relevant > Key Entities > AI Keywords.
export const HIGHLIGHT_CATEGORIES = [
    { id: "definitions", label: "Definitions", dataKey: "definitions", kind: "obj" },
    { id: "exam_relevant", label: "Exam Relevant", dataKey: "exam_relevant" },
    { id: "entities", label: "Key Entities", dataKey: "entities" },
    { id: "domain", label: "Domain Terms", dataKey: "domain_terms", dynamic: true },
    { id: "numbers", label: "Dates & Numbers", dataKey: null }, // client-side regex
    { id: "concepts", label: "Core Concepts", dataKey: "concepts" },
    { id: "keywords", label: "AI Keywords", dataKey: "keywords" },
];

export const DOMAIN_LABEL = { medical: "Medical Terms", legal: "Legal Terms", business: "Business Terms" };

// Subtle by default: only the highest-value layers on, so the page never gets noisy.
export const DEFAULT_LAYERS = {
    definitions: true,
    concepts: true,
    exam_relevant: true,
    entities: false,
    keywords: false,
    numbers: false,
    domain: true,
};

const MONTHS = new Set([
    "january", "february", "march", "april", "may", "june", "july", "august",
    "september", "october", "november", "december",
    "januar", "februar", "märz", "april", "mai", "juni", "juli", "august",
    "september", "oktober", "november", "dezember",
]);

const NUMERIC_RE = /^[€$£¥]?[+-]?\d[\d.,:/'\u2013-]*\d?\s?(%|st|nd|rd|th|k|m|bn|mrd|kg|mg|g|ml|l|cm|mm|km|€|\$|°c?|°f?)?$/i;

function stripEdges(w) {
    return (w || "").replace(/^[^\p{L}\p{N}€$£¥%]+/u, "").replace(/[^\p{L}\p{N}%]+$/u, "");
}

export function isDateOrNumber(word) {
    const w = stripEdges(word);
    if (!w) return false;
    if (MONTHS.has(w.toLowerCase())) return true;
    if (!/\d/.test(w)) return false;
    return NUMERIC_RE.test(w);
}

function norm(w) {
    return stripEdges(w).toLowerCase();
}

function spansForCategory(catId, data) {
    if (!data) return [];
    if (catId === "definitions") return (data.definitions || []).map((d) => d?.span || "").filter(Boolean);
    if (catId === "domain") return data.domain_terms || [];
    if (catId === "numbers") return null; // regex-driven
    const cat = HIGHLIGHT_CATEGORIES.find((c) => c.id === catId);
    return (cat && data[cat.dataKey]) || [];
}

/**
 * Build a Map<tokenIndex, categoryId> for the enabled layers.
 * Higher-priority categories win on overlap. Verbatim span matching is
 * case-insensitive and punctuation-tolerant; occurrences per span are capped so
 * a common word can't flood the page.
 */
export function buildHighlightIndex(model, data, enabledLayers) {
    const result = new Map();
    if (!data || !model?.tokens?.length) return result;

    const tokens = model.tokens;
    const normTokens = tokens.map((t) => norm(t.text));

    // index first-token -> positions for faster span search
    const firstTokenPos = new Map();
    normTokens.forEach((nt, i) => {
        if (!nt) return;
        if (!firstTokenPos.has(nt)) firstTokenPos.set(nt, []);
        firstTokenPos.get(nt).push(i);
    });

    const assign = (start, end, catId) => {
        for (let i = start; i <= end; i++) {
            if (!result.has(i)) result.set(i, catId);
        }
    };

    for (const cat of HIGHLIGHT_CATEGORIES) {
        if (!enabledLayers[cat.id]) continue;

        if (cat.id === "numbers") {
            for (let i = 0; i < tokens.length; i++) {
                if (!result.has(i) && isDateOrNumber(tokens[i].text)) result.set(i, "numbers");
            }
            continue;
        }

        const spans = spansForCategory(cat.id, data);
        for (const span of spans) {
            const parts = String(span).split(/\s+/).map(norm).filter(Boolean);
            if (!parts.length) continue;
            const starts = firstTokenPos.get(parts[0]) || [];
            let hits = 0;
            for (const s of starts) {
                if (hits >= 3) break; // cap occurrences per span
                let ok = true;
                for (let k = 1; k < parts.length; k++) {
                    if (normTokens[s + k] !== parts[k]) { ok = false; break; }
                }
                if (ok) {
                    assign(s, s + parts.length - 1, cat.id);
                    hits += 1;
                }
            }
        }
    }
    return result;
}

/** Which categories actually have content for this document (for the UI). */
export function availableCategories(data) {
    if (!data) return [];
    return HIGHLIGHT_CATEGORIES.filter((cat) => {
        if (cat.id === "numbers") return true;
        if (cat.id === "domain") return data.domain && data.domain !== "general" && (data.domain_terms || []).length > 0;
        return (spansForCategory(cat.id, data) || []).length > 0;
    }).map((cat) => ({
        ...cat,
        label: cat.id === "domain" ? (DOMAIN_LABEL[data.domain] || "Domain Terms") : cat.label,
        count: cat.id === "numbers" ? null : (spansForCategory(cat.id, data) || []).length,
    }));
}


// ------------------------ click-to-explain helpers ------------------------ //

/** The sentence text that contains a given token (context for Click-to-Explain). */
export function sentenceContextForToken(model, tokenIndex) {
    const tok = model?.tokens?.[tokenIndex];
    if (!tok) return "";
    const s = model.sentences[tok.sentenceIndex];
    if (!s) return tok.text;
    return model.tokens.slice(s.tokenStart, s.tokenEnd + 1).map((x) => x.text).join(" ").trim();
}

/**
 * Expand a clicked highlighted token into its full verbatim span (the whole
 * multi-word highlight), plus the sentence it sits in as context. Returns null
 * when the token is not highlighted.
 */
export function expandHighlightSpan(model, highlightIndex, tokenIndex) {
    const cat = highlightIndex?.get?.(tokenIndex);
    if (!cat) return null;
    const sIdx = model.tokens[tokenIndex]?.sentenceIndex;
    let start = tokenIndex;
    let end = tokenIndex;
    while (start - 1 >= 0 && highlightIndex.get(start - 1) === cat && model.tokens[start - 1].sentenceIndex === sIdx) start--;
    while (end + 1 < model.tokens.length && highlightIndex.get(end + 1) === cat && model.tokens[end + 1].sentenceIndex === sIdx) end++;
    const term = model.tokens.slice(start, end + 1).map((x) => x.text).join(" ").trim();
    return { term, context: sentenceContextForToken(model, tokenIndex) };
}


// -------------------------- smart highlight presets ----------------------- //
// Outcome-based one-click presets. They ONLY toggle which cached spans render —
// they never trigger a new AI call. `on` lists the categories a preset enables.
const PRESET_CATS = ["definitions", "concepts", "exam_relevant", "entities", "domain", "numbers", "keywords"];

export const HIGHLIGHT_PRESETS = [
    { id: "firstReading", on: ["definitions", "concepts"] },
    { id: "revision", on: ["exam_relevant", "definitions", "concepts", "numbers"] },
    { id: "medical", on: ["definitions", "exam_relevant", "domain", "numbers"], domain: "medical" },
    { id: "law", on: ["definitions", "exam_relevant", "domain", "entities"], domain: "legal" },
    { id: "business", on: ["definitions", "concepts", "exam_relevant", "numbers", "entities"], domain: "business" },
];

/** Full layer object for a preset id (all known categories set true/false). */
export function presetLayers(presetId) {
    const p = HIGHLIGHT_PRESETS.find((x) => x.id === presetId);
    if (!p) return null;
    const out = {};
    for (const id of PRESET_CATS) out[id] = p.on.includes(id);
    return out;
}

/** Which preset (if any) the current layer selection exactly matches, else "custom". */
export function presetForLayers(layers) {
    if (!layers) return "custom";
    for (const p of HIGHLIGHT_PRESETS) {
        if (PRESET_CATS.every((id) => !!layers[id] === p.on.includes(id))) return p.id;
    }
    return "custom";
}

/** The preset that matches a clearly-detected document domain (or null). */
export function recommendedPreset(data) {
    const d = data?.domain;
    if (!d || d === "general") return null;
    const p = HIGHLIGHT_PRESETS.find((x) => x.domain === d);
    return p ? p.id : null;
}


// ------------------------ smart highlights overview ----------------------- //

function normPhrase(s) {
    return String(s || "").split(/\s+/).map(norm).filter(Boolean).join(" ");
}

/**
 * Flat, deduped list of highlighted spans for the Smart Highlights overview.
 * Groups consecutive same-category tokens into spans, dedupes by text (keeping
 * the first occurrence for jump + a count), and tags every enabled category the
 * span belongs to (so a multi-category term shows once with several labels).
 */
export function buildHighlightOverview(model, data, enabledLayers) {
    const hi = buildHighlightIndex(model, data, enabledLayers);
    if (!hi.size || !model?.tokens?.length) return [];

    // Precompute phrase -> categories once (cheap multi-category lookup).
    const catByPhrase = new Map();
    for (const cat of HIGHLIGHT_CATEGORIES) {
        if (!enabledLayers[cat.id] || cat.id === "numbers") continue;
        for (const sp of spansForCategory(cat.id, data) || []) {
            const np = normPhrase(sp);
            if (!np) continue;
            if (!catByPhrase.has(np)) catByPhrase.set(np, new Set());
            catByPhrase.get(np).add(cat.id);
        }
    }

    const toks = model.tokens;
    const raw = [];
    let i = 0;
    while (i < toks.length) {
        const cat = hi.get(i);
        if (!cat) { i++; continue; }
        const sIdx = toks[i].sentenceIndex;
        let j = i;
        while (j + 1 < toks.length && hi.get(j + 1) === cat && toks[j + 1].sentenceIndex === sIdx) j++;
        raw.push({ text: toks.slice(i, j + 1).map((x) => x.text).join(" ").trim(), primary: cat, tokenStart: i });
        i = j + 1;
    }

    const map = new Map();
    for (const s of raw) {
        const key = s.text.toLowerCase();
        if (!key) continue;
        if (!map.has(key)) {
            const extra = new Set(catByPhrase.get(normPhrase(s.text)) || []);
            if (enabledLayers.numbers && String(s.text).split(/\s+/).some(isDateOrNumber)) extra.add("numbers");
            extra.delete(s.primary);
            map.set(key, {
                text: s.text,
                primary: s.primary,
                cats: [s.primary, ...extra],
                tokenStart: s.tokenStart,
                count: 1,
                context: sentenceContextForToken(model, s.tokenStart),
            });
        } else {
            const e = map.get(key);
            e.count += 1;
            if (!e.cats.includes(s.primary)) e.cats.push(s.primary);
        }
    }
    return [...map.values()].sort((a, b) => a.tokenStart - b.tokenStart);
}
